import React, {useCallback} from 'react';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import trackAnalytics from '../../../utils/gaia/trackAnalytics';
import theme from '../../../theme';
import {TrackDispatcher} from '../../../hooks/useTracks';
import TrackStats from '../TrackStats';
import ElevationProfile from '../ElevationProfile';
import OutlinedSelect from '../OutlinedSelect';
import MenuItem from '@material-ui/core/MenuItem';
import {snackbarZIndex} from '../../../utils/gaia/zIndicies';
import {SPRING_1} from '../../../colors';
import {makeStyles} from '@material-ui/core/styles';

interface TrackSnackbarProps {
    onInteractionChange: (interaction: MapInteraction) => void;
    interaction: MovingTrackMapInteraction;
    trackDispatcher: TrackDispatcher;
    tracks: Record<string, Track>;
    project: MapProject;
    onPopupChange: (popup: MapPopup) => void;
    onHighlightLocationChange: SetState<mapboxgl.LngLat | undefined>;
}

const useStyles = makeStyles({
    notchedOutline: {borderColor: 'rgba(255, 255, 255, 0.23) !important'},
    icon: {color: '#fff !important'},
    outlined: {color: '#fff !important'},
});

export default function TrackSnackbar({
    interaction,
    tracks,
    project,
    onPopupChange,
    trackDispatcher,
    onInteractionChange,
    onHighlightLocationChange,
}: TrackSnackbarProps) {
    const classes = useStyles();
    const track = tracks[interaction.trackLocalId];
    const mapboxLayerId = interaction.mapboxLayerId;

    const onRoutingModeChange = useCallback(event => {
        if (interaction.type !== 'editingRoute' || !track) return;
        trackDispatcher({
            type: 'update',
            track: {
                ...track,
                properties: {
                    ...track.properties,
                    routing_mode: event.target.value as Track['properties']['routing_mode'],
                },
            },
        });
    }, [interaction.type, track, trackDispatcher]);

    if (!track) return null;

    return <Snackbar
        style={{
            marginBottom: theme.spacing(3),
            zIndex: snackbarZIndex,
            left: '50%',
            right: 'auto',
            transform: 'translateX(-50%)',
            position: 'absolute'
        }}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={interaction.type === 'editingRoute'}
        message={<div style={{display: 'flex', flexDirection: 'row', width: 433}}>
            <ElevationProfile
                project={project}
                track={track}
                interaction={interaction}
                width={275}
                height={150}
                onHighlightLocationChange={onHighlightLocationChange}
                dataColor={SPRING_1}
            />

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginLeft: theme.spacing(1)
            }}>
                <TrackStats track={track} project={project} interaction={interaction} />

                <OutlinedSelect
                    FormControlProps={{style: {marginTop: theme.spacing(2)}}}
                    label={<span style={{color: '#fff'}}>Routing Mode</span>}
                    value={track.properties.routing_mode}
                    margin="dense"
                    onChange={onRoutingModeChange}
                    OutlinedInputProps={{classes: {notchedOutline: classes.notchedOutline}}}
                    classes={{icon: classes.icon, outlined: classes.outlined}}
                >
                    <MenuItem value="pedestrian">Hiking</MenuItem>
                    <MenuItem value="bicycle">Cycling</MenuItem>
                    <MenuItem value="auto">Driving</MenuItem>
                    <MenuItem value="point-to-point">Straight Line</MenuItem>
                </OutlinedSelect>

                <div>
                    <Button
                        color="secondary"
                        onClick={onCancelClick}
                        variant="outlined"
                        style={{
                            fontSize: 14,
                            marginRight: theme.spacing(1),
                            color: theme.palette.secondary.main,
                            marginTop: theme.spacing(1),
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={onDoneClick}
                        variant="contained"
                        style={{
                            fontSize: 14,
                            color: '#fff',
                            marginTop: theme.spacing(1),
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        Save
                    </Button>
                </div>
            </div>

        </div>}
    />;

    function onDoneClick() {
        if (interaction.type !== 'editingRoute') return;
        trackAnalytics('Save Route', {category: 'map2'});
        trackDispatcher({type: 'update', track: tracks[interaction.trackLocalId], save: true});
        onInteractionChange({type: 'normal', subtype: 'normal'});
        onPopupChange({
            type: 'track',
            trackLocalId: interaction.trackLocalId,
            mapboxLayerId,
        });
    }

    function onCancelClick() {
        if (interaction.type !== 'editingRoute') return;
        trackAnalytics('Cancel Route Edit', {category: 'map2'});
        onInteractionChange({type: 'normal', subtype: 'normal'});
        if (interaction.initialTrack) {
            trackDispatcher({type: 'update', track: interaction.initialTrack});
            onPopupChange({
                type: 'track',
                trackLocalId: interaction.trackLocalId,
                mapboxLayerId,
            });
        } else {
            trackDispatcher({type: 'remove', trackLocalId: interaction.trackLocalId, save: true});
        }
    }
}
